import { CustomerInfo } from '@/domain/CustomerInfo'
import FileData from '@/domain/FileData'
import { AgencyInfo } from '@/domain/AgencyInfo'
import { InspectionInfo } from '@/domain/InspectionInfo'
import { PriceInfo } from '@/domain/PriceInfo'
import { SumInfo } from '@/domain/SumInfo'
import { UserInfo } from '@/domain/UserInfo'
import DateUtils from '@/utils/DateUtils'

/**
 * 依頼明細
 */
export class RequestDetail {
    /** 検査番号 */
    inspactNo = ""
    /** 検査情報 */
    inspectionInfo = new InspectionInfo()
    /* 価格情報 */
    priceInfo = new PriceInfo()
    /* 報告番号 */
    reportNo = ""
    /* 報告日 */
    reportOn = ""
    /* 請求番号 */
    invoiceNo = ""
    /* 請求日 */
    invoiceOn = ""

    /**
     * コンストラクタ
     * @param init 
     */
    constructor(init?: Partial<RequestDetail>) {
        Object.assign(this, init)
    }
}

/**
 * 依頼
 */
export class Request {
    /** 受付番号 */
    requestNo = ""
    /** 依頼日 YYYY-MM-DD */
    requestOn = ""
    /** 件名 */
    subject = ""
    /** 検査依頼種別 */
    requestType = 0
    /** 顧客情報 */
    customerInfo = new CustomerInfo()
    /** 代理店情報 */
    agencyInfo = new AgencyInfo()
    /** 検体名称 */
    specimenName = ""
    /** 検体識別記号 */
    specimenId = ""
    /** 集計情報 */
    sumInfo = new SumInfo()
    /** 検体着予定日 */
    arriveOn = ""
    /** 検体着日 */
    receiptOn = ""
    /** 検体受取ユーザー */
    receiptUser = new UserInfo()
    /** 納品予定日 */
    deliveryOn = ""
    /** 重量 */
    weight: number | null = null
    /** 検体の返却 0:不明 1:要返却 2:返却不要 */
    specimenReturn = 0
    /** 備考 */
    note = ""
    /** 検査開始日 */
    inspectionOn = ""
    /** 検査開始ユーザー */
    inspectionUser = new UserInfo()
    /** 報告完了 */
    reportCompleted = false
    /** 請求完了 */
    invoiceCompleted = false
    /** NDFP検査依頼ID'*/
    requestId = ""

    /** 依頼明細 */
    details = [new RequestDetail()]

    /** 見積番号 */
    estimateNo = ""

    /** 請書番号 */
    confirmationNo = ""

    /* 請求番号 */
    invoiceNos: string[] = []

    /** 報告書作成日 YYYY-MM-DD */
    reportOn = ""
    
    /** 依頼書 */
    requestFile = new FileData()

    /** 前の受付番号 */
    previousRequestNo = ""

    /** 次の受付番号 */
    nextRequestNo = ""

    /** 報告書数 */
    reportCount = 0

    /** 送信済み報告書数 */
    reportSendCount = 0

    /**
     * コンストラクタ
     * @param init 
     */
    constructor(init?: Partial<Request>) {
        Object.assign(this, init)
    }

    /**
     * 複製を作成する
     */
    clone() {
        return new Request(JSON.parse(JSON.stringify(this)));
    }

    /**
     * 宛先の会社が空か
     */
    get isEmptyCustomer() {
        return this.customerInfo.customerPrefectureCode === "" || this.customerInfo.customerCode === "" || this.customerInfo.customerBranchNumber === ""
    }

    /**
     * 代理店経由か
     */
    get hasAgency() {
        return this.agencyInfo.agencyPrefectureCode !== null && this.agencyInfo.agencyCode !== null || this.agencyInfo.agencyBranchNumber !== null
    }

    /**
     * ロック状態(編集不可)か？
     */
    get isLocked() {
        return this.confirmationNo !== "" || this.invoiceNos.length > 0 || this.reportOn !== ""
    }

    /**
     * 小計
     */
    get amount() {
        return this.details
            .map(x => x.priceInfo.unitPrice * x.priceInfo.quantity)
            .reduce((p, x) => p + x, 0)
    }

    /**
     * 小計（カンマ区切り）
     */
    get amountText() {
        return isNaN(this.amount) ? "-" : this.amount.toLocaleString()
    }

    /**
     * 消費税
     */
    get tax() {
        return this.details
            .map(x => Math.floor(x.priceInfo.unitPrice * x.priceInfo.quantity * (x.priceInfo.taxRate / 100)))
            .reduce((p, x) => p + x, 0)
    }

    /**
     * 消費税（カンマ区切り）
     */
    get taxText() {
        return isNaN(this.tax) ? "-" : this.tax.toLocaleString()
    }

    /**
     * 合計金額
     */
    get totalAmount() {
        return this.amount + this.tax
    }

    /**
     * 合計金額（カンマ区切り）
     */
    get totalAmountText() {
        return isNaN(this.totalAmount) ? "-" : this.totalAmount.toLocaleString()
    }

    /**
     * フィルターキーワードの取得
     */
    get filterKeyword() {
        const dateUtils = DateUtils.instance
        return `${dateUtils.toJapanFormat(this.requestOn)} ${this.requestNo} ${this.customerInfo.customerName} ${this.agencyInfo.agencyName} ${this.specimenName} ${this.specimenId} ${dateUtils.toJapanFormat(this.arriveOn)} ${this.weight} ${dateUtils.toJapanFormat(this.receiptOn)} ${this.inspectionUser.userName} ${this.sumInfo.inspectionNames} ${this.sumInfo.classificationNames} ${dateUtils.toJapanFormat(this.deliveryOn)} ${dateUtils.toJapanFormat(this.reportOn)}`
    }

    /**
     * 明細行を追加
     * @param detail
     */
    addDetailLine(detail = new RequestDetail()) {
        this.details.push(detail)
    }

    /**
     * 明細行を削除
     * @param lineIndex
     */
    deleteDeailLine(lineIndex: number) {
        this.details.splice(lineIndex, 1)
        if (this.details.length === 0) {
            this.addDetailLine()
        }
    }
}
